<app-uni-dialog [parent]="this" [contentTemplate]="contentTemplate" [footerTemplate]="footerTemplate"></app-uni-dialog>

<ng-template #contentTemplate>
  <div *ngIf="isPageReady" class="flex flex-col h-full">

    <!-- The export is the only record that survives this. It has to come first, and it has to
         read as a step rather than a note, because there is no way back after the save. -->
    <div class="mx-3 mt-3 mb-2 p-3 qb-detail-card" style="border-color: var(--p-orange-500, #f97316)">
      <div class="text-sm font-semibold mb-1" style="color: var(--p-orange-600, #ea580c)">
        <i class="pi pi-file-excel mr-1"></i>Do this first: export the errors to Excel
      </div>
      <div class="text-sm">
        Close this window and press <b>Export Errors</b> on the Manufacturer Feed page. Save the file.
        A style that you retire leaves the error list, and the system cannot make the file again.
        The file is the only record of what you retired and why the manufacturer rejected it.
      </div>
    </div>

    <!-- Retiring is not a local tidy-up. It travels all the way out. -->
    <div class="mx-3 mb-2 p-3 qb-detail-card" style="border-color: var(--p-red-500, #ef4444)">
      <div class="text-sm font-semibold mb-1" style="color: var(--p-red-500, #ef4444)">
        <i class="pi pi-exclamation-triangle mr-1"></i>This change goes all the way through to Proximity
      </div>
      <div class="text-sm">
        Everything you retire here goes on Phaseout and is sent to Proximity on the next push.
        No one can order it. The work in the Open Work columns finishes.
        If you retire a style, no product of that style can be ordered.
        If you retire the last live product on a style, the system puts that style on Phaseout too.
        When the open work is done, the row shows on the Phaseout Drain page (References).
        A product admin makes it Inactive there.
      </div>
    </div>

    <!-- Centred and named. The scan walks every product and every staged part for the
         manufacturer, so on SanMar this holds for a while and has to look deliberate. -->
    <div *ngIf="isScanning" class="min-h-0 w-full flex-1 flex flex-col items-center justify-center gap-4">
      <!-- pi-spin, not p-progressSpinner: the PrimeNG component renders an SVG whose stroke
           animation does not paint under this theme, and the app's own spinner CSS still targets
           the v17 class name. This is the spinner the sweep progress line uses. -->
      <i class="pi pi-spin pi-spinner" aria-label="loading"
         style="font-size: 56px; color: var(--p-primary-color, #0077c5)"></i>
      <div class="text-base font-semibold">The system reads the candidates.</div>
      <div class="text-sm" style="color: var(--p-text-muted-color)">
        The system checks every style, every color and size, and all the stock and open orders
        behind them. For a large manufacturer this takes a minute.
      </div>
    </div>

    <div *ngIf="!isScanning" class="min-h-0 w-full flex-1">
      <p-tabView [(activeIndex)]="activeTab" (onChange)="onTabChange($event)"
                 class="uni-tabview h-full w-full" styleclass="h-full">

        <p-tabPanel [header]="'Styles Not Found in Feed (' + styles.length + ')'">
          <div class="flex h-full flex-col">
            <div class="qb-child-actions shrink-0 flex items-center gap-3 flex-wrap">
              <span class="text-sm">
                <span class="font-semibold">{{ retirableStyles.length }}</span> ready to retire
                <ng-container *ngIf="blockedStyles.length > 0">
                  &nbsp;|&nbsp;
                  <span class="font-semibold" style="color: var(--p-red-500, #ef4444)">
                    {{ blockedStyles.length }}
                  </span> need a different fix (see Result)
                </ng-container>
              </span>
            </div>
            <div class="min-h-0 w-full flex-1">
              <app-ag-grid [gridOptions]="styleGridOptions"></app-ag-grid>
            </div>
          </div>
        </p-tabPanel>

        <p-tabPanel [header]="'Products Not Found in Feed (' + products.length + ')'">
          <div class="flex h-full flex-col">
            <div class="qb-child-actions shrink-0 flex items-center gap-3 flex-wrap">
              <span class="text-sm">
                <span class="font-semibold">{{ retirableProducts.length }}</span> ready to retire
              </span>
            </div>
            <div class="min-h-0 w-full flex-1">
              <app-ag-grid [gridOptions]="productGridOptions"></app-ag-grid>
            </div>
          </div>
        </p-tabPanel>
      </p-tabView>
    </div>

    <div *ngIf="isApplying" class="px-3 pt-2 text-sm">
      The system puts the selection on Phaseout. {{ progressDone }} of {{ progressTotal }} done.
    </div>

    <div *ngIf="!isApplying && (retiredStyles > 0 || retiredProducts > 0)" class="px-3 pt-2 text-sm font-semibold">
      The system put {{ retiredStyles }} style(s) and {{ retiredProducts }} product(s) on Phaseout.
    </div>

    <div *ngIf="errorMessage" class="px-3 pt-2 text-sm" style="color: var(--p-red-500, #ef4444)">
      {{ errorMessage }}
    </div>
  </div>
</ng-template>

<ng-template #footerTemplate>
  <div class="flex flex-row gap-2">
    <button pButton type="button" class="p-button-raised" icon="pi pi-check-square"
            [disabled]="isApplying || isScanning" (click)="tickAllRetirable()">
      <span>Select Every Ready Row</span>
    </button>
    <button pButton type="button" class="p-button-raised" icon="pi pi-times"
            [disabled]="isApplying || isScanning" (click)="clearTicks()">
      <span>Clear</span>
    </button>
    <button pButton type="button" class="p-button-raised" icon="pi pi-ban"
            [disabled]="!canApply" [loading]="isApplying" (click)="onApply()">
      <span>Retire the Selected Rows</span>
    </button>
    <button pButton type="button" class="p-button-raised" [disabled]="isApplying" (click)="onClose()">
      <span>Close</span>
    </button>
  </div>
</ng-template>
